import apiClient from '../utils/apiClient';

class TaxonomyModel {
  /**
   * Get the tenant ID from session
   */
  static getTenantId() {
    const user = JSON.parse(sessionStorage.getItem('user'));
    return user?.tenant_ids?.[0];
  }

  /**
   * Taxonomy CRUD operations
   */
  static async getAllTaxonomies() {
    const tenantId = this.getTenantId();
    return await apiClient(`/taxonomies/${tenantId}`);
  }

  static async getTaxonomyById(id) {
    const tenantId = this.getTenantId();
    return await apiClient(`/taxonomies/${tenantId}/${id}`);
  }

  static async createTaxonomy(taxonomyData) {
    const tenantId = this.getTenantId();
    return await apiClient(`/taxonomies/${tenantId}`, {
      method: 'POST',
      data: taxonomyData
    });
  }

  static async updateTaxonomy(id, taxonomyData) {
    const tenantId = this.getTenantId();
    return await apiClient(`/taxonomies/${tenantId}/${id}`, {
      method: 'PUT',
      data: taxonomyData
    });
  }

  static async deleteTaxonomy(id) {
    const tenantId = this.getTenantId();
    return await apiClient(`/taxonomies/${tenantId}/${id}`, {
      method: 'DELETE'
    });
  }

  /**
   * Term CRUD operations
   */
  static async getTermsByTaxonomyId(taxonomyId) {
    const tenantId = this.getTenantId();
    return await apiClient(`/taxonomies/${tenantId}/${taxonomyId}/terms`);
  }

  static async getTermById(termId) {
    const tenantId = this.getTenantId();
    return await apiClient(`/taxonomies/${tenantId}/terms/${termId}`);
  }

  static async createTerm(taxonomyId, termData) {
    const tenantId = this.getTenantId();
    return await apiClient(`/taxonomies/${tenantId}/${taxonomyId}/terms`, {
      method: 'POST',
      data: termData
    });
  }

  static async updateTerm(termId, termData) {
    const tenantId = this.getTenantId();
    return await apiClient(`/taxonomies/${tenantId}/terms/${termId}`, {
      method: 'PUT',
      data: termData
    });
  }

  static async deleteTerm(termId) {
    const tenantId = this.getTenantId();
    return await apiClient(`/taxonomies/${tenantId}/terms/${termId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Term-Content relationship operations
   */
  static async associateTermWithContent(termId, contentId) {
    const tenantId = this.getTenantId();
    return await apiClient(`/taxonomies/${tenantId}/terms/${termId}/content`, {
      method: 'POST',
      data: {
        content_id: contentId
      }
    });
  }

  static async removeTermFromContent(termId, contentId) {
    const tenantId = this.getTenantId();
    return await apiClient(`/taxonomies/${tenantId}/terms/${termId}/content/${contentId}`, {
      method: 'DELETE'
    });
  }

  static async getTermsByContentId(contentId) {
    const tenantId = this.getTenantId();
    return await apiClient(`/taxonomies/${tenantId}/content/${contentId}/terms`);
  }

  static async getContentByTermId(termId) {
    const tenantId = this.getTenantId();
    return await apiClient(`/taxonomies/${tenantId}/terms/${termId}/content`);
  }

  /**
   * Helper functions
   */
  static async slugify(text) {
    return text
      .toString()
      .toLowerCase()
      .replace(/\s+/g, '-')        // Replace spaces with -
      .replace(/[^\w-]+/g, '')     // Remove all non-word chars
      .replace(/--+/g, '-')        // Replace multiple - with single -
      .replace(/^-+/, '')          // Trim - from start of text
      .replace(/-+$/, '');         // Trim - from end of text
  }

  static async generateSlug(name, taxonomyId, existingTermId = null) {
    // Generate base slug
    const baseSlug = await this.slugify(name);
    
    try {
      // Get all existing terms to check for slug conflicts
      const tenantId = this.getTenantId();
      const allTerms = await this.getTermsByTaxonomyId(taxonomyId);
      
      // Filter out the current term if we're updating
      const otherTerms = allTerms.filter(term => existingTermId ? term.id !== existingTermId : true);
      
      // Check if slug exists
      const existingSlugs = otherTerms.map(term => term.slug);
      if (!existingSlugs.includes(baseSlug)) {
        return baseSlug;
      }
      
      // If slug exists, add a number
      let counter = 1;
      let newSlug = `${baseSlug}-${counter}`;
      
      while (existingSlugs.includes(newSlug)) {
        counter++;
        newSlug = `${baseSlug}-${counter}`;
      }
      
      return newSlug;
    } catch (error) {
      // If error occurs during slug checking, return the base slug with timestamp
      return `${baseSlug}-${Date.now()}`;
    }
  }
}

export default TaxonomyModel;
